import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  foldLookupName,
  type LookupCreateInput,
  type LookupOption,
  type WineLookupKind,
  type WineWorld,
} from '@gwc/shared';
import { lookupsApi } from '../api/lookups';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { ApiError } from '../lib/api';

export interface LookupValue {
  id?: string;
  name: string;
}

/**
 * Typeahead over one wine lookup list (producers, regions/communes, vineyards,
 * varietals). Picking a row sets its id; typing sets just the name, which the
 * server resolves (or adds, flagged for review) when the RSVP is saved. The
 * last row — `Not in the list? Add "…"` — adds it right away and selects it.
 */
export function LookupTypeahead({
  kind,
  value,
  onChange,
  world,
  regionId,
  allowCreate = true,
  createExtras,
  label,
  placeholder,
  disabled,
  required,
}: {
  kind: WineLookupKind;
  value: LookupValue;
  onChange: (v: LookupValue) => void;
  /** Regions: only this side's. Also passed on create. */
  world?: WineWorld | null;
  /** Vineyards: rank this region's first, and place a new one in it. */
  regionId?: string | null;
  allowCreate?: boolean;
  /** Extra fields for "Add" (e.g. a varietal's colour). */
  createExtras?: Omit<LookupCreateInput, 'name'>;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}) {
  const inputId = useId();
  const listId = `${inputId}-list`;
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [picked, setPicked] = useState<LookupOption | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();
  const text = value.name;
  const q = useDebouncedValue(text.trim(), 250);

  // A pick made elsewhere (form reset, another wine loaded) drops our badge.
  useEffect(() => {
    if (picked && picked.id !== value.id) setPicked(null);
  }, [value.id, picked]);

  const { data: options = [], isFetching } = useQuery({
    queryKey: ['lookup-search', kind, q, world ?? null, regionId ?? null],
    queryFn: () =>
      lookupsApi.search(kind, {
        q: q || undefined,
        world: world ?? undefined,
        regionId: regionId ?? undefined,
        limit: 12,
      }),
    enabled: open && !disabled,
    staleTime: 30_000,
  });

  const folded = foldLookupName(text);
  const exact = options.find((o) => foldLookupName(o.name) === folded);
  const showAdd = allowCreate && folded.length > 0 && !exact && !isFetching;
  const rowCount = options.length + (showAdd ? 1 : 0);

  useEffect(() => setHighlight(0), [q, options.length]);

  function pick(o: LookupOption) {
    clearTimeout(blurTimer.current);
    setPicked(o);
    setCreateError(null);
    onChange({ id: o.id, name: o.name });
    setOpen(false);
  }

  async function add() {
    clearTimeout(blurTimer.current);
    setCreating(true);
    setCreateError(null);
    try {
      const res = await lookupsApi.create(kind, {
        name: text.trim(),
        ...(kind === 'regions' && world ? { world } : {}),
        ...(kind === 'vineyards' && regionId ? { regionId } : {}),
        ...createExtras,
      });
      pick(res.data);
    } catch (e) {
      // Keep the typed name: the server can still resolve it when the RSVP is saved.
      setCreateError(e instanceof ApiError ? e.message : 'Could not add that just now');
      setOpen(false);
    } finally {
      setCreating(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!open || rowCount === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, rowCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = options[highlight];
      if (o) pick(o);
      else if (showAdd) void add();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const pendingReview = picked?.id === value.id && picked?.needsReview;

  return (
    <div className="relative">
      <label htmlFor={inputId} className="mb-1 block text-sm text-cream-200">
        {label}
        {required && <span className="text-wine-300"> *</span>}
      </label>
      <input
        id={inputId}
        className="field"
        value={text}
        placeholder={placeholder}
        disabled={disabled || creating}
        autoComplete="off"
        role="combobox"
        aria-expanded={open && rowCount > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        onChange={(e) => {
          setCreateError(null);
          onChange({ name: e.target.value });
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        onKeyDown={onKeyDown}
      />
      {pendingReview && (
        <p className="mt-1 text-xs text-cream-300/50">
          <span className="rounded-full border border-warning/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-warning">
            awaiting review
          </span>{' '}
          Added by a member — an admin will check the spelling.
        </p>
      )}
      {createError && <p className="mt-1 text-xs text-wine-300">{createError}</p>}
      {open && rowCount > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-ink-800 bg-ink-950 py-1 shadow-xl"
        >
          {options.map((o, i) => (
            <li key={o.id} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                className={`flex w-full items-baseline gap-2 px-3 py-2.5 text-left text-sm ${
                  i === highlight ? 'bg-ink-800 text-gold-200' : 'text-cream-200 hover:bg-ink-800'
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o);
                }}
                onMouseEnter={() => setHighlight(i)}
              >
                <span>{o.name}</span>
                {o.parentName && (
                  <span className="truncate text-xs text-cream-300/50">— {o.parentName}</span>
                )}
                {o.needsReview && (
                  <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-warning/80">
                    awaiting review
                  </span>
                )}
              </button>
            </li>
          ))}
          {showAdd && (
            <li role="option" aria-selected={highlight === options.length}>
              <button
                type="button"
                className={`block w-full border-t border-ink-800 px-3 py-2.5 text-left text-sm ${
                  highlight === options.length
                    ? 'bg-ink-800 text-gold-200'
                    : 'text-cream-300 hover:bg-ink-800'
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  void add();
                }}
                onMouseEnter={() => setHighlight(options.length)}
              >
                Not in the list? Add &ldquo;{text.trim()}&rdquo;
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
